import { useEffect, useState } from "react";
import Icon1 from "@assets/icons/Icon";
import { ordresApi } from "@services/api/ordres";
import type { PendingAPrevoir } from "@services/api/ordres";

/**
 * Surfaces the dossier's pending "à prévoir" interventions (saved on a previous visit)
 * so they can be re-proposed — pre-checked — when creating the next RDV for the vehicle.
 * Selection is lifted to the parent via `onSelectionChange`; the parent builds the new
 * ordre's interventions from it and calls `ordresApi.consumeAPrevoir` after creation.
 */
export default function PendingAPrevoirPanel({
  dossierId,
  vin,
  excludeOrdreId,
  onSelectionChange,
}: {
  dossierId?: string;
  vin?: string;
  excludeOrdreId?: string;
  onSelectionChange: (selected: PendingAPrevoir[]) => void;
}) {
  const [pending, setPending] = useState<PendingAPrevoir[]>([]);
  const [checked, setChecked] = useState<Record<string, boolean>>({});
  const [loading, setLoading] = useState(false);

  const keyOf = (p: PendingAPrevoir) => `${p.ordre_id}:${p.intervention.id}`;

  useEffect(() => {
    if (!dossierId) { setPending([]); return; }
    let cancelled = false;
    setLoading(true);
    ordresApi.getPendingAPrevoir({ dossier_id: dossierId, vin, exclude_ordre_id: excludeOrdreId })
      .then((items) => {
        if (cancelled) return;
        setPending(items);
        // Pre-check everything (garage-internal proposal).
        const init: Record<string, boolean> = {};
        items.forEach((p) => { init[`${p.ordre_id}:${p.intervention.id}`] = true; });
        setChecked(init);
      })
      .catch(() => { if (!cancelled) setPending([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [dossierId, vin, excludeOrdreId]);

  useEffect(() => {
    onSelectionChange(pending.filter((p) => checked[keyOf(p)]));
  }, [pending, checked, onSelectionChange]);

  if (!dossierId || loading || pending.length === 0) return null;

  const selectedCount = pending.filter((p) => checked[keyOf(p)]).length;

  return (
    <div className="rounded-lg border border-amber-200 bg-amber-50/50 overflow-hidden">
      <div className="flex flex-row items-center gap-2 px-3 py-2 border-b border-amber-200">
        <Icon1 name="flag" size={14} color="#B45309" />
        <span className="text-h4 font-semibold text-amber-800">
          Travaux à prévoir sur ce véhicule ({pending.length})
        </span>
      </div>
      <div className="flex flex-col gap-1 p-2">
        {pending.map((p) => {
          const k = keyOf(p);
          return (
            <label key={k} className="flex flex-row items-center gap-2 px-2 py-1.5 rounded-md hover:bg-amber-100/50 cursor-pointer">
              <input
                type="checkbox"
                checked={!!checked[k]}
                onChange={(e) => setChecked((prev) => ({ ...prev, [k]: e.target.checked }))}
                className="accent-amber-600"
              />
              <div className="flex flex-col flex-1 min-w-0">
                <span className="text-h4 font-medium text-neutral-700 truncate">{p.intervention.label}</span>
                <span className="text-h5 text-neutral-400">
                  {p.intervention.estimated_hours}h estimée{p.ordre_num ? ` · devis OR n°${p.ordre_num}` : ""}
                </span>
              </div>
            </label>
          );
        })}
      </div>
      <div className="px-3 py-1.5 text-h5 text-amber-700 border-t border-amber-200">
        {selectedCount > 0
          ? `${selectedCount} travail(aux) seront ajoutés à ce RDV`
          : "Aucun travail à prévoir sélectionné"}
      </div>
    </div>
  );
}
